import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { 
  FiCheckCircle, 
  FiXCircle, 
  FiLoader,
  FiArrowLeft,
  FiHome
} from 'react-icons/fi';
import { SeoHead } from '../utils/seo';
import { verifyPayment, verifyKorapayPayment } from '../store/slices/paymentSlice';
import { getProfile } from '../store/slices/authSlice';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

export const PaymentCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');
  const [paymentGateway, setPaymentGateway] = useState('');
  const hasVerified = useRef(false); // Prevent double verification

  const reference = searchParams.get('reference');
  const trxref = searchParams.get('trxref');

  useEffect(() => {
    const verifyTransaction = async () => {
      // Prevent double verification
      if (hasVerified.current) return;
      
      const ref = reference || trxref;
      
      if (!ref) {
        setStatus('failed');
        setMessage('No transaction reference found');
        return;
      }

      try {
        hasVerified.current = true;
        
        // Detect payment gateway based on reference prefix
        let result;
        if (ref.startsWith('KORA_')) {
          setPaymentGateway('korapay');
          result = await dispatch(verifyKorapayPayment(ref));
        } else {
          setPaymentGateway('paystack');
          result = await dispatch(verifyPayment(ref));
        }
        
        if (verifyPayment.fulfilled.match(result) || verifyKorapayPayment.fulfilled.match(result)) {
          await dispatch(getProfile()).unwrap().catch(() => null);
          setStatus('success');
          setMessage('Payment verified successfully! Your wallet has been credited.');
          toast.success('Payment successful!');
        } else {
          setStatus('failed');
          setMessage(result.payload?.message || 'Payment verification failed');
          toast.error('Payment verification failed');
        }
      } catch (error) {
        setStatus('failed');
        setMessage('An error occurred while verifying your payment');
        toast.error('Verification error');
      }
    };

    verifyTransaction();
  }, [dispatch, reference, trxref]); // Empty dependency array means it runs once

  const pageTitle = 'Payment Status | Nitro Logs';
  const pageDescription = 'Check the status of your Nitro Logs payment and payment verification after checkout or wallet funding via Paystack or Korapay.';

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        keywords="payment status, Nitro Logs payment verification, Paystack payment, Korapay payment"
      />

      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-linear-to-br from-luxury-50 via-white to-luxury-100">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full"
        >
          {/* Status Card */}
          <div className="glass rounded-2xl p-8 text-center">
            {/* Status Icon */}
            <div className="mb-6">
              {status === 'processing' && (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                  className="w-20 h-20 mx-auto bg-primary-100 rounded-full flex items-center justify-center"
                >
                  <FiLoader className="w-10 h-10 text-primary-600" />
                </motion.div>
              )}

              {status === 'success' && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', damping: 10 }}
                  className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center"
                >
                  <FiCheckCircle className="w-10 h-10 text-green-600" />
                </motion.div>
              )}

              {status === 'failed' && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', damping: 10 }}
                  className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center"
                >
                  <FiXCircle className="w-10 h-10 text-red-600" />
                </motion.div>
              )}
            </div>

            {/* Status Title */}
            <h2 className="text-2xl font-display font-bold text-luxury-900 mb-2">
              {status === 'processing' && 'Verifying Payment'}
              {status === 'success' && 'Payment Successful!'}
              {status === 'failed' && 'Payment Failed'}
            </h2>

            {/* Status Message */}
            <p className="text-luxury-600 mb-6">
              {message || 'Please wait while we verify your transaction...'}
            </p>

            {/* Transaction Reference */}
            {(reference || trxref) && (
              <div className="bg-luxury-100 p-3 rounded-lg mb-6">
                <p className="text-xs text-luxury-500 mb-1">Transaction Reference</p>
                <p className="text-sm font-mono text-luxury-900 break-all">
                  {reference || trxref}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {status === 'success' && (
                <>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full luxury-button"
                  >
                    Go to Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/accounts')}
                    className="w-full luxury-button-outline"
                  >
                    Browse Accounts
                  </button>
                </>
              )}

              {status === 'failed' && (
                <>
                  <button
                    onClick={() => navigate('/deposit')}
                    className="w-full luxury-button"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => navigate('/support')}
                    className="w-full luxury-button-outline"
                  >
                    Contact Support
                  </button>
                </>
              )}

              {status === 'processing' && (
                <div className="flex justify-center space-x-3">
                  <Link to="/">
                    <button className="flex items-center space-x-2 px-4 py-2 glass rounded-xl hover:bg-primary-50 transition-colors">
                      <FiHome className="w-4 h-4" />
                      <span>Home</span>
                    </button>
                  </Link>
                  <Link to="/dashboard">
                    <button className="flex items-center space-x-2 px-4 py-2 glass rounded-xl hover:bg-primary-50 transition-colors">
                      <FiArrowLeft className="w-4 h-4" />
                      <span>Dashboard</span>
                    </button>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Help Text */}
          <p className="text-center text-sm text-luxury-500 mt-6">
            Need help?{' '}
            <Link to="/contact" className="text-primary-600 hover:text-primary-700">
              Contact Support
            </Link>
          </p>
        </motion.div>
      </div>
    </>
  );
};